import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    title?: string
  }
}

const routes: RouteRecordRaw[] = [
  { path: '/login', name: 'Login', component: () => import('@/views/LoginView.vue'), meta: { title: 'Sign In' } },
  {
    path: '/',
    name: 'Dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { requiresAuth: true, title: 'Dashboard' },
  },
  {
    path: '/maintenance',
    name: 'Maintenance',
    component: () => import('@/views/MaintenanceView.vue'),
    meta: { requiresAuth: true, title: 'API Log' },
  },
  {
    path: '/battery-health',
    name: 'BatteryHealth',
    component: () => import('@/views/BatteryHealthView.vue'),
    meta: { requiresAuth: true, title: 'Battery Health' },
  },
  {
    path: '/trips',
    name: 'Trips',
    component: () => import('@/views/TripsView.vue'),
    meta: { requiresAuth: true, title: 'Trip History' },
  },
  {
    path: '/trips/:id',
    name: 'TripDetail',
    component: () => import('@/views/TripDetailView.vue'),
    meta: { requiresAuth: true, title: 'Trip Detail' },
  },
  {
    path: '/travel-log',
    name: 'TravelLog',
    component: () => import('@/views/TravelLogView.vue'),
    meta: { requiresAuth: true, title: 'Travel Log' },
  },
  {
    path: '/charge-sessions',
    name: 'ChargeSessions',
    component: () => import('@/views/ChargeSessionsView.vue'),
    meta: { requiresAuth: true, title: 'Charge History' },
  },
  {
    path: '/charge-sessions/:id',
    name: 'ChargeSessionDetail',
    component: () => import('@/views/ChargeSessionDetailView.vue'),
    meta: { requiresAuth: true, title: 'Charge Session Detail' },
  },
  {
    path: '/charging-ecosystem',
    name: 'ChargingEcosystem',
    component: () => import('@/views/ChargingEcosystemView.vue'),
    meta: { requiresAuth: true, title: 'Charging Ecosystem' },
  },
  {
    path: '/geographic-efficiency',
    name: 'GeographicEfficiency',
    component: () => import('@/views/GeographicEfficiencyView.vue'),
    meta: { requiresAuth: true, title: 'Geographic Efficiency' },
  },
  {
    path: '/ai-insights',
    name: 'AiInsights',
    component: () => import('@/views/AiInsightsView.vue'),
    meta: { requiresAuth: true, title: 'AI Insights' },
  },
  {
    path: '/energy',
    name: 'EnergyIntelligence',
    component: () => import('@/views/EnergyIntelligenceView.vue'),
    meta: { requiresAuth: true, title: 'Energy Intelligence' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Page Not Found' },
  },
]

const router = createRouter({ history: createWebHistory(), routes })

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  if (to.name === 'Login') {
    // Only redirect to Dashboard when fully authenticated; never during verifying to prevent loops
    if (auth.verifyStatus === 'authenticated' && !auth.isExpired) return { name: 'Dashboard' }
    return
  }

  if (!to.meta.requiresAuth) return

  // If verification is in progress, await its outcome
  if (auth.verifyStatus === 'verifying' || auth.verifyStatus === 'unknown') {
    await auth.ensureVerified()
  }

  // Allow through only when authenticated and token is not expired
  if (auth.isAuthenticated && !auth.isExpired) return

  return { name: 'Login' }
})

router.afterEach((to) => {
  const title = typeof to.meta.title === 'string' ? to.meta.title : ''
  document.title = title ? `${title} — Hyundai Ioniq 6` : 'Hyundai Ioniq 6'
})

export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err)
  return /dynamically imported module|Failed to fetch dynamically|Importing a module script failed|ChunkLoadError/i.test(msg)
}

const CHUNK_RELOAD_KEY = 'chunk-reload-at'

export function guardedChunkReload(): void {
  const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? '0')
  if (Date.now() - last < 10_000) return
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
  window.location.reload()
}

router.onError((err) => {
  if (!isChunkLoadError(err)) return
  guardedChunkReload()
})

export default router
